import { debounce } from 'lodash';
import { CloudOff, Loader2, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { OrgLogoId, OrgLogos } from '@crossnokaye/ux-animatics';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  InputWithIcon,
  NotificationBadge,
} from '@crossnokaye/ux-common';

import { useFilteredOrgFacilities } from '@/components/navigation/hooks/use-filtered-org-facilities';
import { useOfflineFacilities } from '@/components/navigation/hooks/use-offline-facilities';
import { SquareButton } from '@/components/navigation/left/components/shared/square-button';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useNavStore } from '@/stores/nav';
import { useCurrentFeature } from '@/utils/routing';

export const FacilityMoreMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { enableFacilityMenu, orgIcon, setFacilitySearchQuery, facilitySearchQuery } =
    useNavStore();

  const { offlineFacilities, getOfflineFacilityCountByOrg } = useOfflineFacilities();

  const { filteredOrgFacilityList } = useFilteredOrgFacilities();

  const { facilityId: currentFacilityId } = useCurrentFacility();
  const navigate = useNavigate();
  const currentFeature = useCurrentFeature();

  useEffect(() => {
    if (isOpen) {
      setInputValue(facilitySearchQuery);
    }
  }, [isOpen, facilitySearchQuery]);

  const handleSelect = (e: React.MouseEvent, orgName: string, shortName: string) => {
    e.stopPropagation();

    navigate({
      to: `/$org/$agent/${currentFeature}`,
      params: { org: orgName, agent: shortName },
    });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFacilitySearchQuery(value);
      }, 300),
    [setFacilitySearchQuery]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    debouncedSearch(newValue);
  };

  const handleClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue('');
    setFacilitySearchQuery('');
  };
  const OrgLogoIcon = OrgLogos[orgIcon as OrgLogoId];

  return (
    <div>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger
          asChild
          disabled={!enableFacilityMenu}
          data-testid={'collapsed-nav-facility-selector'}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={cn(
              'flex justify-center bg-popover w-full py-2.5 my-2',
              enableFacilityMenu && 'cursor-pointer hover:bg-tertiary',
              isOpen && 'bg-tertiary'
            )}
          >
            {OrgLogoIcon ? (
              <OrgLogoIcon size={'2rem'} color={'white'} />
            ) : (
              <Loader2
                className={'animate-spin'}
                data-testid={'collapsed-nav-facility-selector-loader'}
              />
            )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={'border-input border-1 min-w-48 z-overlay'}
          side={'right'}
          align={'start'}
          onKeyDown={(e) => e.stopPropagation()}
          data-testid={'collapsed-nav-facility-selector-content'}
          withPortal
        >
          <div className={'m-2.5'}>
            <div className={'relative'}>
              <InputWithIcon
                className={'text-foreground h-6 bg-tertiary'}
                icon={<Search size={'1rem'} />}
                placeholder={'Search Facilities'}
                onChange={handleSearch}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                value={inputValue}
                data-testid={'collapsed-nav-facility-selector-search'}
              />
              {inputValue && (
                <SquareButton
                  icon={<X size={'1rem'} />}
                  onClick={(e) => handleClearSearch(e)}
                  className={
                    'absolute right-2 top-1/2 -translate-y-1/2 bg-tertiary text-muted-foreground hover:text-foreground hover:bg-popover h-4 w-4'
                  }
                />
              )}
            </div>
          </div>
          <DropdownMenuSeparator />
          <div className={'overflow-y-auto max-h-[66vh] scrollbar-hide'}>
            {filteredOrgFacilityList?.map((org, index) => {
              const offlineFacilityCount = getOfflineFacilityCountByOrg(org.organizationId);
              const OrgLogoIcon = OrgLogos[org.iconName as OrgLogoId];

              return (
                <React.Fragment key={org.organizationId}>
                  <DropdownMenuLabel
                    className={'text-muted-foreground uppercase flex justify-start text-xs'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {OrgLogoIcon && (
                      <OrgLogoIcon size={'1rem'} className={'mr-1'} color={'white'} />
                    )}
                    <div>{org.displayName}</div>
                    {offlineFacilityCount > 0 && (
                      <NotificationBadge
                        className={'bg-warning w-3.5 h-3.5 text-black ml-auto text-2xs'}
                        count={offlineFacilityCount}
                      />
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {(org.facilities || []).map((item) => (
                      <DropdownMenuItem
                        key={item.facilityId}
                        onClick={(e) => handleSelect(e, org.name, item.shortName)}
                        className={cn(
                          'cursor-pointer',
                          item.facilityId === currentFacilityId &&
                            'bg-primary/20 text-primary hover:bg-primary/30'
                        )}
                        data-testid={`collapsed-nav-facility-selector-item-${item.facilityId}`}
                      >
                        <FacilityNavItem
                          displayName={item.displayName}
                          isOffline={offlineFacilities.has(item.facilityId)}
                        />
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                  {index < (filteredOrgFacilityList?.length ?? 0) - 1 && <DropdownMenuSeparator />}
                </React.Fragment>
              );
            })}
            {filteredOrgFacilityList?.length === 0 && (
              <DropdownMenuItem className={'text-muted-foreground'}>
                {'No matching facilities found.'}
              </DropdownMenuItem>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const FacilityNavItem = ({
  displayName,
  isOffline,
}: {
  displayName: string;
  isOffline: boolean;
}) => {
  return (
    <div className={'flex items-center justify-between w-full'}>
      <span className={cn('truncate', isOffline ? 'text-warning' : '')}>{displayName}</span>
      {isOffline && <CloudOff size={'1rem'} className={'ml-2 flex-shrink-0 text-warning'} />}
    </div>
  );
};
